import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { login } from '../../store/auth/auth.actions';
import { selectAuthLoading, selectAuthError, selectSignInOutcome } from '../../store/auth/auth.selectors';

@Component({
  selector: 'app-sign-in',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './sign-in.html',
  styleUrls: ['./sign-in.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignIn implements OnInit, OnDestroy {
  private store = inject(Store);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);

  form: FormGroup;
  hide = true;

  // NgRx observables
  loading$ = this.store.select(selectAuthLoading);
  error$ = this.store.select(selectAuthError);
  // Single composite selector (not combineLatest over two independent
  // store.select() streams) so isAuthenticated + hasAllowedRole always
  // arrive together in one emission — avoids a transient
  // [authenticated, not-yet-allowed] state causing a spurious navigation
  // to /access-denied on every successful admin login.
  signInOutcome$ = this.store.select(selectSignInOutcome);

  // Local state for template binding
  loading = false;
  serverError: string | null = null;

  private sub = new Subscription();

  constructor() {
    this.form = this.fb.group({
      emailOrMobileNo: ['', [Validators.required]],
      password: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    // Subscribe to store state changes
    this.sub.add(
      this.loading$.subscribe(loading => {
        this.loading = loading;
        this.cdr.markForCheck();
      })
    );

    this.sub.add(
      this.error$.subscribe(error => {
        if (error) {
          this.serverError = error;
          this.toastr.error(error, 'Login Failed');
        }
        this.cdr.markForCheck();
      })
    );

    this.sub.add(
      this.signInOutcome$
        .pipe(filter(({ isAuthenticated }) => isAuthenticated))
        .subscribe(({ hasAllowedRole }) => {
          if (!hasAllowedRole) {
            // Authenticated but not an admin — RBAC gate blocks access
            // to the rest of the application.
            this.router.navigateByUrl('/access-denied');
            return;
          }
          this.toastr.success('Logged in successfully!');
          this.router.navigateByUrl('/dashboard');
        })
    );
  }

  get emailOrMobileNo(): AbstractControl { return this.form.get('emailOrMobileNo')!; }
  get password(): AbstractControl { return this.form.get('password')!; }

  showError(ctrl: AbstractControl | null): boolean {
    return !!ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched);
  }

  togglePassword(): void { 
    this.hide = !this.hide; 
    this.cdr.markForCheck();
  }
  
  onSubmit(): void {
    // Mark all fields as touched to show validation errors
    this.form.markAllAsTouched();
    this.cdr.markForCheck();
    
    if (this.form.invalid || this.loading) {
      return;
    }

    this.serverError = null;

    const email = String(this.emailOrMobileNo.value ?? '').trim();
    const password = String(this.password.value ?? '').trim();

    // Dispatch NgRx action
    this.store.dispatch(login({ email, password }));
  }

  onForgotPassword(): void {
    const emailOrMobileNo = String(this.emailOrMobileNo.value ?? '').trim();
    this.router.navigate(['/forgot-password'], { 
      state: { emailOrMobileNo } 
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
